/**
 * 📋 Маппинг типов событий аналитики
 * 
 * Определяет соответствие между кодами событий, их названиями
 * и форматом отображения деталей
 */

export type EventDetailType = 'empty' | 'string' | 'number' | 'object'

export interface EventMapping {
  code: string
  name: string
  detailType: EventDetailType
  detailLabel?: string // Метка для деталей (например, "ID якоря")
  detailFormatter?: (value: any) => string // Кастомный форматтер для объектов
}

// ========================================
// Вспомогательные функции для форматирования
// ========================================

/** Форматировать позицию (x, y, z) */
const formatPosition = (pos: { x: number; y: number; z: number }) => 
  `(${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`

/** Форматировать размер файла */
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

/** Форматировать время в миллисекундах */
const formatTime = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes === 0) return `${remainingSeconds} с`
  if (remainingSeconds === 0) return `${minutes} мин`
  return `${minutes} мин ${remainingSeconds} с`
}

/**
 * Маппинг всех известных событий
 */
export const EVENT_MAPPINGS: Record<string, EventMapping> = {
  // ========================================
  // EMPTY - События без деталей
  // ========================================
  'Empty': {
    code: 'Empty',
    name: 'Пустое событие',
    detailType: 'empty',
  },
  'pointcloud_upload_started': {
    code: 'pointcloud_upload_started',
    name: 'Загрузка облака точек начата',
    detailType: 'empty',
  },
  'scanning_started': {
    code: 'scanning_started',
    name: 'Сканирование начато',
    detailType: 'empty',
  },
  'scanning_complete': {
    code: 'scanning_complete',
    name: 'Сканирование завершено',
    detailType: 'empty',
  },
  'surface_scanned_and_placer_positioned': {
    code: 'surface_scanned_and_placer_positioned',
    name: 'Поверхность отсканирована и размещена',
    detailType: 'empty',
  },
  'copilot_opened': {
    code: 'copilot_opened',
    name: 'Копилот открыт',
    detailType: 'empty',
  },
  'copilot_session_started': {
    code: 'copilot_session_started',
    name: 'Сессия копилота начата',
    detailType: 'empty',
  },
  'copilot_session_cleared': {
    code: 'copilot_session_cleared',
    name: 'Сессия копилота очищена',
    detailType: 'empty',
  },
  'knowledge_base_opened': {
    code: 'knowledge_base_opened',
    name: 'База знаний открыта',
    detailType: 'empty',
  },
  'logout': {
    code: 'logout',
    name: 'Выход из системы',
    detailType: 'empty',
  },
  'registration_completed': {
    code: 'registration_completed',
    name: 'Регистрация завершена',
    detailType: 'empty',
  },
  'auth_completed': {
    code: 'auth_completed',
    name: 'Авторизация завершена',
    detailType: 'empty',
  },
  'app_started': {
    code: 'app_started',
    name: 'Приложение запущено',
    detailType: 'empty',
  },
  'app_paused': {
    code: 'app_paused',
    name: 'Приложение приостановлено',
    detailType: 'empty',
  },
  'app_resumed': {
    code: 'app_resumed',
    name: 'Приложение возобновлено',
    detailType: 'empty',
  },
  'app_closed': {
    code: 'app_closed',
    name: 'Приложение закрыто',
    detailType: 'empty',
  },

  // ========================================
  // STRING - События с текстовыми деталями
  // ========================================
  'pointcloud_upload_error': {
    code: 'pointcloud_upload_error',
    name: 'Ошибка загрузки облака точек',
    detailType: 'string',
    detailLabel: 'Ошибка',
  },
  'copilot_message_sent': {
    code: 'copilot_message_sent',
    name: 'Сообщение отправлено',
    detailType: 'string',
    detailLabel: 'Сообщение',
  },
  'copilot_response_received': {
    code: 'copilot_response_received',
    name: 'Ответ получен',
    detailType: 'string',
    detailLabel: 'Ответ',
  },
  'copilot_response_error': {
    code: 'copilot_response_error',
    name: 'Ошибка ответа',
    detailType: 'string',
    detailLabel: 'Ошибка',
  },

  // ========================================
  // NUMBER - События с числовыми деталями
  // ========================================
  'pointcloud_upload_completed': {
    code: 'pointcloud_upload_completed',
    name: 'Загрузка облака точек завершена',
    detailType: 'number',
    detailLabel: 'ID якоря',
  },
  'resolve_by_stencil': {
    code: 'resolve_by_stencil',
    name: 'Разрешение по трафарету',
    detailType: 'number',
    detailLabel: 'ID инструкции',
  },
  'objects_list_loaded': {
    code: 'objects_list_loaded',
    name: 'Список объектов загружен',
    detailType: 'number',
    detailLabel: 'Количество объектов',
  },
  'scanning_progress': {
    code: 'scanning_progress',
    name: 'Прогресс сканирования',
    detailType: 'number',
    detailLabel: 'Прогресс [0.0 - 1.0]',
  },

  // ========================================
  // OBJECT - События с объектными деталями
  // ========================================
  'aruco_recognized': {
    code: 'aruco_recognized',
    name: 'Распознан ArUco маркер',
    detailType: 'object',
    detailFormatter: (v) => v?.position ? `Позиция: ${formatPosition(v.position)}` : '—',
  },
  'document_deleted': {
    code: 'document_deleted',
    name: 'Документ удалён',
    detailType: 'object',
    detailFormatter: (v) => v?.fileName ? `Файл: ${v.fileName}` : '—',
  },
  'document_added': {
    code: 'document_added',
    name: 'Документ добавлен',
    detailType: 'object',
    detailFormatter: (v) => v?.fileName ? `Файл: ${v.fileName}` : '—',
  },
  'step_created': {
    code: 'step_created',
    name: 'Шаг создан',
    detailType: 'object',
    detailFormatter: (v) => 'Данные шага',
  },
  'step_attached': {
    code: 'step_attached',
    name: 'Вложение прикреплено',
    detailType: 'object',
    detailFormatter: (v) => v?.position ? `Позиция: ${formatPosition(v.position)}` : '—',
  },
  'scenario_created': {
    code: 'scenario_created',
    name: 'Процедура создана',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `${v.name} (${v.stepsCount || 0} шагов)` : '—',
  },
  'video_background_uploading_start': {
    code: 'video_background_uploading_start',
    name: 'Загрузка фонового видео начата',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.fileSize) parts.push(`Размер: ${formatFileSize(v.fileSize)}`)
      return parts.join(', ') || '—'
    },
  },
  'video_background_uploading_end': {
    code: 'video_background_uploading_end',
    name: 'Загрузка фонового видео завершена',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.fileSize) parts.push(`Размер: ${formatFileSize(v.fileSize)}`)
      return parts.join(', ') || '—'
    },
  },
  'video_background_uploading_error': {
    code: 'video_background_uploading_error',
    name: 'Ошибка загрузки фонового видео',
    detailType: 'object',
    detailFormatter: (v) => v?.error ? `Ошибка: ${v.error}` : v?.fileName || '—',
  },
  'run_scenario': {
    code: 'run_scenario',
    name: 'Запуск процедуры',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Процедура: ${v.name}` : '—',
  },
  'scenario_ended': {
    code: 'scenario_ended',
    name: 'Процедура завершена',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.isCompleted !== undefined) {
        parts.push(v.isCompleted ? 'Завершён' : 'Прерван')
      }
      if (v?.time) parts.push(`Время: ${formatTime(v.time)}`)
      if (v?.currentStep !== undefined && v?.totalSteps !== undefined) {
        parts.push(`Шаги: ${v.currentStep}/${v.totalSteps}`)
      }
      return parts.join(', ') || '—'
    },
  },
  'scenario_completed': {
    code: 'scenario_completed',
    name: 'Процедура пройдена',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.name) parts.push(`Процедура: ${v.name}`)
      if (v?.time) parts.push(`Время: ${formatTime(v.time)}`)
      if (v?.currentStep !== undefined && v?.totalSteps !== undefined) {
        parts.push(`Шаги: ${v.currentStep}/${v.totalSteps}`)
      }
      return parts.join(', ') || '—'
    },
  },
  'step_started': {
    code: 'step_started',
    name: 'Шаг начат',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.name) parts.push(`Шаг: ${v.name}`)
      if (v?.scenarioName) parts.push(`Процедура: ${v.scenarioName}`)
      return parts.join(', ') || '—'
    },
  },
  'step_completed': {
    code: 'step_completed',
    name: 'Шаг завершён',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.name) parts.push(`Шаг: ${v.name}`)
      if (v?.time) parts.push(`Время: ${formatTime(v.time)}`)
      if (v?.scenarioName) parts.push(`Процедура: ${v.scenarioName}`)
      return parts.join(', ') || '—'
    },
  },
  'scenario_creating_start': {
    code: 'scenario_creating_start',
    name: 'Создание процедуры начато',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.name) parts.push(`Название: ${v.name}`)
      if (v?.type) {
        const typeText = v.type === 'manual' ? 'Обычная' : v.type === 'video' ? 'На основе видео' : v.type
        parts.push(`Тип: ${typeText}`)
      }
      return parts.join(', ') || '—'
    },
  },
  'step_edited': {
    code: 'step_edited',
    name: 'Шаг отредактирован',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.name) parts.push(`Шаг: ${v.name}`)
      if (v?.oldDescription && v?.newDescription) {
        parts.push(`"${v.oldDescription}" → "${v.newDescription}"`)
      }
      if (v?.scenarioName) parts.push(`Процедура: ${v.scenarioName}`)
      return parts.join(', ') || '—'
    },
  },
  'step_moved': {
    code: 'step_moved',
    name: 'Шаг перемещён',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.name) parts.push(`Шаг: ${v.name}`)
      if (v?.oldPosition && v?.newPosition) {
        parts.push(`${formatPosition(v.oldPosition)} → ${formatPosition(v.newPosition)}`)
      }
      if (v?.scenarioName) parts.push(`Процедура: ${v.scenarioName}`)
      return parts.join(', ') || '—'
    },
  },
  'attachment_upload_started': {
    code: 'attachment_upload_started',
    name: 'Загрузка вложения начата',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.fileSize) parts.push(`Размер: ${formatFileSize(v.fileSize)}`)
      return parts.join(', ') || '—'
    },
  },
  'attachment_upload_completed': {
    code: 'attachment_upload_completed',
    name: 'Загрузка вложения завершена',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.fileSize) parts.push(`Размер: ${formatFileSize(v.fileSize)}`)
      return parts.join(', ') || '—'
    },
  },
  'attachment_upload_error': {
    code: 'attachment_upload_error',
    name: 'Ошибка загрузки вложения',
    detailType: 'object',
    detailFormatter: (v) => v?.error ? `Ошибка: ${v.error}` : v?.fileName || '—',
  },
  'scenario_video_picked': {
    code: 'scenario_video_picked',
    name: 'Видео выбрано',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.size) parts.push(`Размер: ${formatFileSize(v.size)}`)
      return parts.join(', ') || '—'
    },
  },
  'scenario_video_compressed_start': {
    code: 'scenario_video_compressed_start',
    name: 'Сжатие видео начато',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.size && v?.newSize) {
        parts.push(`Размер: ${formatFileSize(v.size)} → ${formatFileSize(v.newSize)}`)
      } else if (v?.size) {
        parts.push(`Размер: ${formatFileSize(v.size)}`)
      }
      return parts.join(', ') || '—'
    },
  },
  'scenario_video_compressed_error': {
    code: 'scenario_video_compressed_error',
    name: 'Ошибка сжатия видео',
    detailType: 'object',
    detailFormatter: (v) => v?.fileName || '—',
  },
  'scenario_video_compressed_end': {
    code: 'scenario_video_compressed_end',
    name: 'Сжатие видео завершено',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.newSize) parts.push(`Размер: ${formatFileSize(v.newSize)}`)
      return parts.join(', ') || '—'
    },
  },
  'scenario_audio_extracted_start': {
    code: 'scenario_audio_extracted_start',
    name: 'Извлечение аудио начато',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.size) parts.push(`Размер: ${formatFileSize(v.size)}`)
      return parts.join(', ') || '—'
    },
  },
  'scenario_audio_extracted_error': {
    code: 'scenario_audio_extracted_error',
    name: 'Ошибка извлечения аудио',
    detailType: 'object',
    detailFormatter: (v) => v?.fileName || '—',
  },
  'scenario_audio_extracted_end': {
    code: 'scenario_audio_extracted_end',
    name: 'Извлечение аудио завершено',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.newSize) parts.push(`Размер: ${formatFileSize(v.newSize)}`)
      return parts.join(', ') || '—'
    },
  },
  'scenario_video_upload_error': {
    code: 'scenario_video_upload_error',
    name: 'Ошибка загрузки видео',
    detailType: 'object',
    detailFormatter: () => 'Ошибка загрузки',
  },
  'scenario_video_process_error': {
    code: 'scenario_video_process_error',
    name: 'Ошибка нарезки видео на шаги',
    detailType: 'object',
    detailFormatter: (v) => v?.error ? `Ошибка: ${v.error}` : '—',
  },
  'scenario_video_process_end': {
    code: 'scenario_video_process_end',
    name: 'Видео нарезано на шаги',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.fileName) parts.push(`Файл: ${v.fileName}`)
      if (v?.size) parts.push(`Размер: ${formatFileSize(v.size)}`)
      if (v?.result) parts.push('Результат нарезки')
      return parts.join(', ') || '—'
    },
  },
  'scenario_edit_opened': {
    code: 'scenario_edit_opened',
    name: 'Редактирование процедуры открыто',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Процедура: ${v.name}` : '—',
  },
  'scenario_preview_opened': {
    code: 'scenario_preview_opened',
    name: 'Предпросмотр процедуры открыт',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Процедура: ${v.name}` : '—',
  },
  'scenario_edit_closed': {
    code: 'scenario_edit_closed',
    name: 'Редактирование процедуры закрыто',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Процедура: ${v.name}` : '—',
  },
  'scenario_preview_closed': {
    code: 'scenario_preview_closed',
    name: 'Предпросмотр процедуры закрыт',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Процедура: ${v.name}` : '—',
  },
  'step_sequence_changed': {
    code: 'step_sequence_changed',
    name: 'Порядок шагов изменён',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.oldSequence && v?.newSequence) {
        parts.push(`Порядок: [${v.oldSequence.join(', ')}] → [${v.newSequence.join(', ')}]`)
      }
      if (v?.name) parts.push(`Процедура: ${v.name}`)
      return parts.join(', ') || '—'
    },
  },
  'scenario_deleted': {
    code: 'scenario_deleted',
    name: 'Процедура удалена',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Процедура: ${v.name}` : '—',
  },
  'scenario_attachment_open': {
    code: 'scenario_attachment_open',
    name: 'Вложение открыто',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.stepNumber !== undefined) parts.push(`Шаг #${v.stepNumber}`)
      if (v?.stepName) parts.push(v.stepName)
      return parts.join(': ') || '—'
    },
  },
  'new_object_created': {
    code: 'new_object_created',
    name: 'Объект создан',
    detailType: 'object',
    detailFormatter: (v) => {
      const parts = []
      if (v?.name) parts.push(`Объект: ${v.name}`)
      if (v?.description) parts.push(`Описание: ${v.description}`)
      return parts.join(', ') || '—'
    },
  },
  'note_moved': {
    code: 'note_moved',
    name: 'Заметка перемещена',
    detailType: 'object',
    detailFormatter: (v) => {
      if (v?.oldPosition && v?.newPosition) {
        return `Позиция: ${formatPosition(v.oldPosition)} → ${formatPosition(v.newPosition)}`
      }
      return '—'
    },
  },
  'note_created': {
    code: 'note_created',
    name: 'Заметка создана',
    detailType: 'object',
    detailFormatter: (v) => v?.content ? `Содержимое: ${v.content}` : '—',
  },
  'note_attached': {
    code: 'note_attached',
    name: 'Заметка прикреплена',
    detailType: 'object',
    detailFormatter: (v) => v?.position ? `Позиция: ${formatPosition(v.position)}` : '—',
  },
  'object_scan_start': {
    code: 'object_scan_start',
    name: 'Сканирование объекта начато',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Объект: ${v.name}` : '—',
  },
  'object_scanned': {
    code: 'object_scanned',
    name: 'Объект отсканирован',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Объект: ${v.name}` : '—',
  },
  'object_open_clicked': {
    code: 'object_open_clicked',
    name: 'Объект открыт',
    detailType: 'object',
    detailFormatter: (v) => v?.name ? `Объект: ${v.name}` : '—',
  },
  'note_changed': {
    code: 'note_changed',
    name: 'Заметка изменена',
    detailType: 'object',
    detailFormatter: (v) => {
      if (v?.oldContent && v?.newContent) {
        return `Содержимое: "${v.oldContent}" → "${v.newContent}"`
      }
      return v?.newContent ? `Содержимое: ${v.newContent}` : '—'
    },
  },
  'note_deleted': {
    code: 'note_deleted',
    name: 'Заметка удалена',
    detailType: 'object',
    detailFormatter: (v) => v?.content ? `Содержимое: ${v.content}` : '—',
  },
}

/**
 * Получить информацию о событии по его коду
 */
export function getEventMapping(eventCode: string): EventMapping | null {
  return EVENT_MAPPINGS[eventCode] || null
}

/**
 * Получить название события по его коду
 */
export function getEventName(eventCode: string): string {
  const mapping = getEventMapping(eventCode)
  return mapping ? mapping.name : eventCode
}

/**
 * Форматировать детали события в зависимости от его типа
 */
export function formatEventDetails(eventCode: string, value: any): string {
  const mapping = getEventMapping(eventCode)
  
  // Парсим JSON, если value - строка
  let parsedValue = value
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      parsedValue = JSON.parse(value)
    } catch (e) {
      // Если не удалось распарсить, оставляем как есть
      console.warn('Не удалось распарсить JSON для события:', eventCode, value)
    }
  }
  
  if (!mapping) {
    // Если маппинг не найден, пытаемся отобразить как есть
    if (parsedValue === null || parsedValue === undefined) return '—'
    if (typeof parsedValue === 'string') return parsedValue
    if (typeof parsedValue === 'object') return JSON.stringify(parsedValue)
    return String(parsedValue)
  }

  switch (mapping.detailType) {
    case 'empty':
      // Для событий без деталей
      return '—'
    
    case 'string':
      // Для строковых данных: "Ошибка: <значение>"
      if (!parsedValue) return '—'
      const stringValue = typeof parsedValue === 'string' ? parsedValue : String(parsedValue)
      return mapping.detailLabel ? `${mapping.detailLabel}: ${stringValue}` : stringValue
    
    case 'number':
      // Для числовых данных: "ID якоря: <число>"
      if (parsedValue === null || parsedValue === undefined) return '—'
      const numValue = typeof parsedValue === 'number' ? parsedValue : Number(parsedValue)
      if (isNaN(numValue)) return '—'
      return mapping.detailLabel ? `${mapping.detailLabel}: ${numValue}` : String(numValue)
    
    case 'object':
      // Для объектных данных - используем кастомный форматтер
      if (!parsedValue) return '—'
      if (mapping.detailFormatter) {
        try {
          return mapping.detailFormatter(parsedValue)
        } catch (e) {
          console.error('Ошибка форматирования события:', eventCode, parsedValue, e)
          return '—'
        }
      }
      // Если форматтера нет, отображаем JSON
      return typeof parsedValue === 'object' ? JSON.stringify(parsedValue) : String(parsedValue)
    
    default:
      return '—'
  }
}

/**
 * Получить список всех событий для автокомплита/фильтрации
 */
export function getAllEventCodes(): string[] {
  return Object.keys(EVENT_MAPPINGS)
}

/**
 * Получить список всех событий с названиями
 */
export function getAllEvents(): Array<{ code: string; name: string }> {
  return Object.values(EVENT_MAPPINGS).map(mapping => ({
    code: mapping.code,
    name: mapping.name,
  }))
}
